import React from 'react'

import {
  ActionIcon,
  Checkbox,
  Group,
  Radio,
  Table,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useField } from '@mantine/form'

import {
  type Revision,
  revisionId,
  type RevisionSet,
} from '#choo-app/lib/editor/plugins/revisions'
import { ColorSwatchPicker } from '#components/ColorPicker'
import { FaIcon } from '#components/FaIcon'
import { SelectList } from '#util/SelectList'

import styles from './RevisionsTable.module.css'

type OptionalSelection<T> = T[] | SelectList<T>

export type WithKey<T extends object> = T & { key: string }

interface RevisionsTableState {
  readonly previous: Revision[]
  readonly active: Revision
  readonly available: OptionalSelection<WithKey<RevisionSet>>
}

export interface RevisionsTableHandle {
  selectLastLabel(): void
}

interface RevisionsTableProps {
  revisions: RevisionsTableState
  onChange(revisions: RevisionsTableState): void
  selectionDisabled?: boolean
  fullDraftDisabled?: boolean
  ref?: React.Ref<RevisionsTableHandle>
}

export const RevisionsTable = ({ ref, ...props }: RevisionsTableProps) => {
  'use memo'

  // Keep refs out of `props` so React Compiler doesn't treat every prop as a
  // ref: https://github.com/facebook/react/issues/37507
  const lastLabelRef = React.useRef<HTMLInputElement | null>(null)

  React.useImperativeHandle(ref, () => ({
    selectLastLabel() {
      lastLabelRef.current?.select()
    },
  }))

  if (!props.revisions) return null

  const available = Array.from(props.revisions.available)
  const indexOfLastAvailable = available.length - 1

  return (
    <Table
      highlightOnHover
      stickyHeader
      withTableBorder
      classNames={{
        thead: styles['t-head'],
        tbody: styles['t-body'],
        tr: styles.row,
      }}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Active set</Table.Th>
          <Table.Th>Label</Table.Th>
          <Table.Th>Full draft</Table.Th>
          <Table.Th>Mark</Table.Th>
          <Table.Th>Text color</Table.Th>
          <Table.Th>Page color</Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {props.revisions.previous.map((revision) => (
          <RevisionSetRow
            key={revisionId(revision)}
            revisionSet={revision}
            fullDraftDisabled={props.fullDraftDisabled}
            onChange={(updated) => {
              props.onChange({
                ...props.revisions,
                previous: props.revisions.previous.map((r) =>
                  r === revision
                    ? { ...updated, snapshot: revision.snapshot }
                    : r,
                ),
              })
            }}
          />
        ))}
        <RevisionSetRow
          revisionSet={props.revisions.active}
          fullDraftDisabled={props.fullDraftDisabled}
          onChange={(updated) =>
            props.onChange({
              ...props.revisions,
              active: { ...updated, snapshot: props.revisions.active.snapshot },
            })
          }
          isSelected={Array.isArray(props.revisions.available)}
          onSelect={
            props.selectionDisabled
              ? undefined
              : () => {
                  props.onChange({ ...props.revisions, available })
                }
          }
        />
        {props.revisions.available.map((revisionSet, index) => (
          <RevisionSetRow
            key={revisionSet.key}
            revisionSet={revisionSet}
            fullDraftDisabled={props.fullDraftDisabled}
            isSelected={
              props.revisions.available instanceof SelectList &&
              props.revisions.available.selected === revisionSet
            }
            onSelect={
              props.selectionDisabled
                ? undefined
                : () =>
                    props.onChange({
                      ...props.revisions,
                      available: new SelectList(
                        available.slice(0, index),
                        revisionSet,
                        available.slice(index + 1),
                      ),
                    })
            }
            onChange={(updated) => {
              props.onChange({
                ...props.revisions,
                available: props.revisions.available.map((revision) =>
                  revision === revisionSet
                    ? { ...updated, key: revision.key }
                    : revision,
                ),
              })
            }}
            onDelete={(deletedRevision) => {
              props.onChange({
                ...props.revisions,
                available: props.revisions.available.filter(
                  (revision) => revision !== deletedRevision,
                ),
              })
            }}
            lastLabelRef={
              index === indexOfLastAvailable ? lastLabelRef : undefined
            }
          />
        ))}
      </Table.Tbody>
    </Table>
  )
}

interface RevisionSetRowProps {
  revisionSet: RevisionSet
  onChange(revisionSet: RevisionSet): void
  isSelected?: boolean
  onSelect?(): void
  onDelete?(revision: RevisionSet): void
  fullDraftDisabled?: boolean
  lastLabelRef?: React.RefObject<HTMLInputElement | null>
}

const RevisionSetRow = ({ lastLabelRef, ...props }: RevisionSetRowProps) => {
  'use memo'

  const labelField = useField({
    initialValue: props.revisionSet.label,
    onValueChange(label) {
      props.onChange({ ...props.revisionSet, label })
    },
  })

  const isFullDraftField = useField({
    type: 'checkbox',
    initialValue: props.revisionSet.isFullDraft,
    onValueChange(isFullDraft) {
      props.onChange({ ...props.revisionSet, isFullDraft })
    },
  })

  const markField = useField({
    initialValue: props.revisionSet.mark,
    onValueChange(mark) {
      props.onChange({ ...props.revisionSet, mark })
    },
  })

  const textColorField = useField({
    initialValue: props.revisionSet.textColor,
    onValueChange(textColor) {
      props.onChange({ ...props.revisionSet, textColor })
    },
  })

  const pageColorField = useField({
    initialValue: props.revisionSet.pageColor,
    onValueChange(pageColor) {
      props.onChange({ ...props.revisionSet, pageColor })
    },
  })

  return (
    <Table.Tr>
      <Table.Td>
        <Group wrap="nowrap" miw={70}>
          <Radio
            disabled={!props.onSelect}
            checked={props.isSelected}
            onChange={props.onSelect}
          />
        </Group>
      </Table.Td>
      <Table.Td>
        <TextInput
          ref={lastLabelRef}
          size="xs"
          inputSize="20"
          {...labelField.getInputProps()}
        />
      </Table.Td>
      <Table.Td>
        <Checkbox
          miw={60}
          {...isFullDraftField.getInputProps()}
          disabled={props.fullDraftDisabled}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          className={styles.mark}
          inputSize="3"
          minLength={1}
          maxLength={3}
          {...markField.getInputProps()}
        />
      </Table.Td>
      <Table.Td>
        <ColorSwatchPicker
          {...textColorField.getInputProps()}
          swatchProps={{ size: 20 }}
          targetProps={{ display: 'block' }}
        />
      </Table.Td>
      <Table.Td>
        <ColorSwatchPicker
          {...pageColorField.getInputProps()}
          swatchProps={{ size: 20 }}
          targetProps={{ display: 'block' }}
        />
      </Table.Td>
      <Table.Td>
        {props.onDelete ? (
          <Tooltip label="Delete revision set" withArrow>
            <ActionIcon
              bg="transparent"
              className={styles.trash}
              onClick={() => props.onDelete?.(props.revisionSet)}
            >
              <FaIcon icon="fa-trash" size="sm" />
            </ActionIcon>
          </Tooltip>
        ) : null}
      </Table.Td>
    </Table.Tr>
  )
}

// oxlint-disable-next-line typescript/no-namespace
export namespace RevisionsTable {
  export type State = RevisionsTableState
  export type Revision = RevisionsTableState['active']
  export type Handle = RevisionsTableHandle
}
